import { useCallback } from 'react';

import { usePartnerCobrandedShareText } from 'src/modules/homeownerApp/hooks/usePartner';
import { useCreateAiDisplayImageShareMutation } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useLocation,
  useNativeShareHandler,
  useTranslate,
} from 'src/shared/hooks';
import { useHasValidNativeShare } from 'src/shared/hooks/useNativeShareHandler';
import { Route as Routes, generatePath } from 'src/shared/route';

import { HomeownerAppMenuNames } from '../providers/HomeownerAppProvider';
import type { AiOrSampleDisplayImage } from '../types';
import { isSampleDisplay } from '../types';

import { useCopyShareHandler, useShareToast } from './useCopyShareHandler';
import { useHomeownerAppContext } from './useHomeownerAppContext';

export interface UseShareGeneratedImageOptions {
  image: AiOrSampleDisplayImage;
}

export const useShareDisplayImage = ({
  image,
}: UseShareGeneratedImageOptions) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { href } = useLocation();

  const { setCurrentMenu } = useHomeownerAppContext();
  const [createAiDisplayImageShare] = useCreateAiDisplayImageShareMutation({
    variables: {
      aiDisplayImageId: String(isSampleDisplay(image) ? '' : image.id),
    },
  });

  const shareText = usePartnerCobrandedShareText();

  const toast = useShareToast();
  const handleCopyShare = useCopyShareHandler();
  const onShareFail = useCallback(
    () =>
      toast({
        description: t('share.messages.fail'),
        status: 'error',
      }),
    [toast, t],
  );

  const isSampleImage = isSampleDisplay(image);

  const handleNativeShare = useNativeShareHandler({
    imageUrl: image.aiGeneratedImage?.generatedImagePath,
    text: shareText,
    title: image.style ?? '',
    onShareFail,
  });
  const hasValidNativeShare = useHasValidNativeShare();

  return useCallback(async () => {
    try {
      if (isSampleImage) {
        setCurrentMenu(HomeownerAppMenuNames.UPLOAD_PHOTO);
        return;
      }
      const { data } = await createAiDisplayImageShare();
      if (!data?.createAiDisplayImageShare?.id) {
        onShareFail();
        return;
      }
      const shareUrl = new URL(
        generatePath(Routes.DISPLAY_IMAGE_SHARE.path, {
          id: data?.createAiDisplayImageShare?.id,
        }),
        href,
      ).href;
      if (hasValidNativeShare) {
        handleNativeShare(shareUrl);
      } else {
        void handleCopyShare({ shareUrl });
      }
    } catch (e) {
      onShareFail();
    }
  }, [
    isSampleImage,
    createAiDisplayImageShare,
    href,
    hasValidNativeShare,
    setCurrentMenu,
    onShareFail,
    handleNativeShare,
    handleCopyShare,
  ]);
};
